/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { api } from 'controllers/network/apiClient';
import { useEffect } from 'react';
import { BANNER_TYPES } from 'utils/events';

interface KlaviyoScopeUpgradeResponse {
  permission_url?: string;
  missing_scopes?: string[];
  // Reason code of a permanent webhook-registration failure (set by
  // klaviyo-service in data.webhook_error). Present -> show reason-specific copy.
  webhook_error_reason?: string;
}

const fetchKlaviyoScopes = () =>
  api.get<KlaviyoScopeUpgradeResponse>(
    '/v2/integrations/klaviyo/scopes/upgrade_url',
  );

interface BannerCopy {
  heading: string;
  bodyText: string;
}

// Copy per webhook_error reason code. Keep in sync with klaviyo-service's
// pkg/installs reason constants.
const REASON_COPY: Record<string, BannerCopy> = {
  reauth_required: {
    heading: 'Reconnect Klaviyo',
    bodyText:
      'Your Klaviyo connection expired or was disconnected. Reconnect to resume syncing events into Postscript.',
  },
  permission_denied: {
    heading: 'Klaviyo connection needs attention',
    bodyText:
      "Postscript couldn't register Klaviyo event webhooks — your Klaviyo account is missing the required permissions or plan access. Reconnect and approve all permissions, or update your Klaviyo plan, then reconnect.",
  },
  unauthorized: {
    heading: 'Klaviyo connection rejected',
    bodyText:
      'Klaviyo rejected the connection. Reconnect, or update your Klaviyo API key, to resume syncing events into Postscript.',
  },
};

// Fallback for the original Back-in-Stock scope-upgrade case (no webhook_error).
const BIS_COPY: BannerCopy = {
  heading: 'Reconnect Klaviyo to enable Back in Stock Emails',
  bodyText:
    'Your Klaviyo connection is missing permissions required to send Back in Stock emails. Reconnect to keep notifications working.',
};

const KlaviyoScopesUpdateBanner = () => {
  const { addBanner } = useBanners();
  const { data } = useQuery({
    queryKey: ['klaviyo-scopes-upgrade'],
    queryFn: fetchKlaviyoScopes,
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
  });
  const { permission_url, missing_scopes, webhook_error_reason } = data || {};

  useEffect(() => {
    if (!permission_url) return;

    const copy =
      (webhook_error_reason && REASON_COPY[webhook_error_reason]) || BIS_COPY;

    // Both problems can be present at once — lead with the webhook-error copy but
    // note the Back-in-Stock scope gap too, so neither is hidden.
    const hasBisGap = (missing_scopes?.length ?? 0) > 0;
    const bodyText =
      webhook_error_reason && hasBisGap
        ? `${copy.bodyText} It's also missing the permissions needed for Back in Stock emails.`
        : copy.bodyText;

    addBanner({
      id: 'KLAVIYO_SCOPES_UPDATE_BANNER',
      announcementType: AnnouncementType.EMERGENCY,
      data: {
        variant: 'default',
        heading: copy.heading,
        bodyText,
        primaryAction: {
          onClick: () => window.open(permission_url, '_self'),
          text: 'Reconnect Klaviyo',
        },
      },
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.KLAVIYO_SCOPES_UPDATE,
      },
    });
  }, [permission_url, missing_scopes, webhook_error_reason]);

  return null;
};

export default KlaviyoScopesUpdateBanner;
